import { useRef } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileText, Download, Copy, Crown } from "lucide-react";
import html2canvas from "html2canvas";

export function DailyReportDialog({ open, onOpenChange, stats, topPerformer }) {
  const reportRef = useRef(null);

  const handleCopyReport = async () => {
    if (!reportRef.current) return;
    try {
      const canvas = await html2canvas(reportRef.current, { backgroundColor: "#0f1a14" });
      canvas.toBlob((blob) => {
        if (blob) navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      });
    } catch (e) { console.error("Failed to copy report:", e); }
  };

  const handleDownloadReport = async () => {
    if (!reportRef.current) return;
    try {
      const canvas = await html2canvas(reportRef.current, { backgroundColor: "#0f1a14" });
      const link = document.createElement("a");
      link.download = `warmap-daily-report-${format(new Date(), "yyyy-MM-dd")}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (e) { console.error("Failed to download report:", e); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1A1A1A] border-white/10 max-w-lg p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" /> Daily Report
          </DialogTitle>
        </DialogHeader>
        <div className="p-6">
          <div ref={reportRef} className="bg-gradient-to-br from-[#0f1a14] to-[#1a2820] rounded-xl p-6 border border-emerald-900/50">
            <div className="flex items-center gap-2 mb-6">
              <Crown className="w-6 h-6 text-emerald-500" />
              <span className="text-lg font-bold text-white font-heading">WarMap Daily Report</span>
            </div>
            <div className="text-sm text-gray-400 mb-4">{format(new Date(), "MMMM d, yyyy")}</div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <ReportMetric label="Cash Collected" value={`$${stats?.cash_collected?.toLocaleString() || 0}`} color="text-emerald-400" />
              <ReportMetric label="Deals Closed" value={stats?.deals_closed || 0} color="text-white" />
              <ReportMetric label="Close Rate" value={`${stats?.close_rate || 0}%`} color="text-yellow-400" />
              <ReportMetric label="AOV" value={`$${stats?.aov?.toLocaleString() || 0}`} color="text-blue-400" />
            </div>
            {topPerformer && (
              <div className="bg-emerald-900/30 rounded-lg p-4 border border-emerald-800/50">
                <div className="text-xs text-emerald-400 uppercase mb-2">Top Performer</div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-700 flex items-center justify-center text-white font-bold">
                    {topPerformer.name?.charAt(0)}
                  </div>
                  <div>
                    <div className="text-white font-medium">{topPerformer.name}</div>
                    <div className="text-sm text-emerald-400">${topPerformer.cash?.toLocaleString()} closed</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-4">
            <Button data-testid="copy-report-btn" onClick={handleCopyReport} className="flex-1 bg-white/10 hover:bg-white/20 text-white">
              <Copy className="w-4 h-4 mr-2" /> Copy Image
            </Button>
            <Button data-testid="download-report-btn" onClick={handleDownloadReport} className="flex-1 bg-gradient-to-r from-emerald-800 to-emerald-600">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportMetric({ label, value, color }) {
  return (
    <div className="bg-black/30 rounded-lg p-4">
      <div className="text-xs text-gray-500 uppercase mb-1">{label}</div>
      <div className={`stat-value text-2xl ${color}`}>{value}</div>
    </div>
  );
}
